import {
    Box,
    VStack,
    Text,
    Button,
    Input,
    Icon,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
} from '@chakra-ui/react';
import {
    TbChevronLeft,
    TbPackage,
    TbCircleCheck,
    TbClock,
    TbCalendar,
    TbTruckDelivery,
} from 'react-icons/tb';
import { IconType } from 'react-icons';
import { useState, useEffect, useRef } from 'react';
import {
    PartOptionFilters,
    PartOptionTypeFilter,
    PART_OPTION_TYPE_FILTERS,
    ValidityFilter,
    DeliveryTimePreset,
    isDeliveryFilterActive,
} from '../utils/partOptionFilters';
import { AddFilterMenu, FilterBar, FilterChip } from '../ui';
import {
    CheckboxMenuRow,
    CheckboxMenuSearch,
    MenuRow,
    MenuFooterActions,
    checkboxMenuTriggerMetrics,
} from '../ui/primitives/CheckboxMenu';

export interface PartOptionAdvancedFiltersProps {
    filters: PartOptionFilters;
    onFiltersChange: (filters: PartOptionFilters) => void;
    availablePartTypes?: PartOptionTypeFilter[];
}

type FilterType = 'partTypes' | 'validity' | 'deliveryTime';
type OpenState = 'addFilter' | FilterType | null;

const FILTER_CATEGORIES: { key: FilterType; label: string; icon: IconType }[] = [
    { key: 'partTypes', label: 'Part Types', icon: TbPackage },
    { key: 'validity', label: 'Validity', icon: TbCircleCheck },
    { key: 'deliveryTime', label: 'Delivery Time', icon: TbClock },
];

const VALIDITY_OPTIONS: { value: ValidityFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'valid', label: 'Valid' },
    { value: 'invalid', label: 'Invalid' },
];

const DELIVERY_TIME_OPTIONS: { value: DeliveryTimePreset; label: string; icon: IconType }[] = [
    { value: 'today', label: 'Today', icon: TbCalendar },
    { value: 'next-day', label: 'Next Day', icon: TbTruckDelivery },
    { value: 'two-days', label: '2 Days', icon: TbClock },
    { value: 'range', label: 'Range', icon: TbClock },
];

const PartOptionAdvancedFilters = ({
    filters,
    onFiltersChange,
    availablePartTypes,
}: PartOptionAdvancedFiltersProps) => {
    const [openState, setOpenState] = useState<OpenState>(null);
    const [addFilterView, setAddFilterView] = useState<FilterType | null>(null);
    const [tempFilters, setTempFilters] = useState<PartOptionFilters>(filters);
    const [filterSearch, setFilterSearch] = useState('');

    const containerRef = useRef<HTMLDivElement>(null);

    const closePopover = () => {
        setOpenState(null);
        setAddFilterView(null);
        setFilterSearch('');
    };

    const togglePopover = (target: OpenState) => {
        if (openState === target) {
            closePopover();
        } else {
            setTempFilters({ ...filters });
            setFilterSearch('');
            if (target === 'addFilter') {
                setAddFilterView(null);
            }
            setOpenState(target);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!openState) return;
            const target = event.target as HTMLElement;
            if (containerRef.current?.contains(target)) return;
            if (target.closest('[data-filter-popover]')) return;
            closePopover();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openState]);

    const handleTempPartTypesChange = (values: (string | number)[]) => {
        setTempFilters((prev) => ({
            ...prev,
            partTypes: values.length > 0 ? (values as PartOptionTypeFilter[]) : undefined,
        }));
    };

    const handleTempValidity = (value: ValidityFilter) => {
        setTempFilters((prev) => {
            const newFilters = { ...prev };
            if (value === 'all') {
                delete newFilters.validity;
            } else {
                newFilters.validity = value;
            }
            return newFilters;
        });
    };

    const handleTempDeliveryTime = (value: DeliveryTimePreset) => {
        setTempFilters((prev) => {
            const newFilters = { ...prev };
            if (prev.deliveryTime === value) {
                delete newFilters.deliveryTime;
                delete newFilters.deliveryDaysRange;
            } else {
                newFilters.deliveryTime = value;
                if (value !== 'range') {
                    delete newFilters.deliveryDaysRange;
                } else {
                    delete newFilters.deliveryDaysRange;
                }
            }
            return newFilters;
        });
    };

    const parseDayInput = (raw: string): number | undefined => {
        if (raw === '') return undefined;
        const parsed = parseInt(raw, 10);
        return Number.isNaN(parsed) ? undefined : parsed;
    };

    const handleTempDeliveryRangeMin = (raw: string) => {
        setTempFilters((prev) => ({
            ...prev,
            deliveryDaysRange: {
                ...prev.deliveryDaysRange,
                min: parseDayInput(raw),
            },
        }));
    };

    const handleTempDeliveryRangeMax = (raw: string) => {
        setTempFilters((prev) => ({
            ...prev,
            deliveryDaysRange: {
                ...prev.deliveryDaysRange,
                max: parseDayInput(raw),
            },
        }));
    };

    const handleApply = () => {
        onFiltersChange(tempFilters);
        closePopover();
    };

    const handleReset = () => {
        setTempFilters({});
        onFiltersChange({});
        closePopover();
    };

    const removeFilter = (filterType: FilterType) => {
        const newFilters = { ...filters };
        switch (filterType) {
            case 'partTypes':
                delete newFilters.partTypes;
                break;
            case 'validity':
                delete newFilters.validity;
                break;
            case 'deliveryTime':
                delete newFilters.deliveryTime;
                delete newFilters.deliveryDaysRange;
                break;
        }
        onFiltersChange(newFilters);
        if (openState === filterType) closePopover();
    };

    const isFilterActive = (filterType: FilterType): boolean => {
        switch (filterType) {
            case 'partTypes':
                return !!(filters.partTypes && filters.partTypes.length > 0);
            case 'validity':
                return !!filters.validity && filters.validity !== 'all';
            case 'deliveryTime':
                return isDeliveryFilterActive(filters);
        }
    };

    const getFilterLabel = (filterType: FilterType): string => {
        switch (filterType) {
            case 'partTypes': {
                const types = filters.partTypes ?? [];
                if (types.length === 0) return 'Part Types';
                if (types.length === 1) return `Part Types: ${types[0]}`;
                return `Part Types: ${types.length} selected`;
            }
            case 'validity': {
                const label = VALIDITY_OPTIONS.find((o) => o.value === filters.validity)?.label ?? 'All';
                return `Validity: ${label}`;
            }
            case 'deliveryTime': {
                const preset = filters.deliveryTime;
                if (!preset) return 'Delivery Time';
                if (preset === 'range') {
                    const range = filters.deliveryDaysRange;
                    if (range?.min != null && range?.max != null) {
                        return `Delivery: ${range.min}–${range.max} days`;
                    }
                    return 'Delivery: Range';
                }
                const label = DELIVERY_TIME_OPTIONS.find((o) => o.value === preset)?.label ?? preset;
                return `Delivery: ${label}`;
            }
        }
    };

    /** Value preview on a drill-in row — the chip label without its category prefix. */
    const getCategorySummary = (filterType: FilterType): string | undefined => {
        if (!isFilterActive(filterType)) return undefined;
        const label = getFilterLabel(filterType);
        const separator = label.indexOf(': ');
        return separator === -1 ? label : label.slice(separator + 2);
    };

    const activeFilters = FILTER_CATEGORIES.filter((cat) => isFilterActive(cat.key));

    const partTypeChoices = availablePartTypes && availablePartTypes.length > 0
        ? availablePartTypes
        : PART_OPTION_TYPE_FILTERS;

    const FilterOption = ({
        label,
        isSelected,
        onClick,
        icon,
    }: {
        label: string;
        isSelected: boolean;
        onClick: () => void;
        icon?: IconType;
    }) => (
        <MenuRow
            kind="single"
            isSelected={isSelected}
            onClick={onClick}
            icon={icon ? <Icon as={icon as React.ElementType} boxSize={4} /> : undefined}
        >
            {label}
        </MenuRow>
    );

    const renderFilterOptions = (filterType: FilterType) => {
        switch (filterType) {
            case 'partTypes': {
                const selectedPartTypes = tempFilters.partTypes ?? [];
                return (
                    <VStack align="stretch" spacing={0} p="6px" maxH="280px" overflowY="auto">
                        {partTypeChoices.map((type) => (
                            <CheckboxMenuRow
                                key={type}
                                isSelected={selectedPartTypes.includes(type)}
                                onClick={() =>
                                    handleTempPartTypesChange(
                                        selectedPartTypes.includes(type)
                                            ? selectedPartTypes.filter((entry) => entry !== type)
                                            : [...selectedPartTypes, type],
                                    )
                                }
                            >
                                {type}
                            </CheckboxMenuRow>
                        ))}
                    </VStack>
                );
            }

            case 'validity':
                return (
                    <VStack align="stretch" spacing={0} py={1}>
                        {VALIDITY_OPTIONS.map(({ value, label }) => (
                            <FilterOption
                                key={value}
                                label={label}
                                isSelected={(tempFilters.validity ?? 'all') === value}
                                onClick={() => handleTempValidity(value)}
                            />
                        ))}
                    </VStack>
                );

            case 'deliveryTime':
                return (
                    <VStack align="stretch" spacing={0} py={1}>
                        {DELIVERY_TIME_OPTIONS.map(({ value, label, icon }) => (
                            <FilterOption
                                key={value}
                                label={label}
                                icon={icon}
                                isSelected={tempFilters.deliveryTime === value}
                                onClick={() => handleTempDeliveryTime(value)}
                            />
                        ))}
                        {tempFilters.deliveryTime === 'range' && (
                            <Box px={3} py={2}>
                                <VStack spacing={2} align="stretch">
                                    <Box>
                                        <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>
                                            MIN DAYS
                                        </Text>
                                        <Input
                                            type="number"
                                            size="sm"
                                            placeholder="Min"
                                            value={
                                                tempFilters.deliveryDaysRange?.min !== undefined
                                                    ? String(tempFilters.deliveryDaysRange.min)
                                                    : ''
                                            }
                                            onChange={(e) => handleTempDeliveryRangeMin(e.target.value)}
                                            bg="white"
                                            borderColor="gray.200"
                                            fontSize={checkboxMenuTriggerMetrics.fontSize}
                                        />
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>
                                            MAX DAYS
                                        </Text>
                                        <Input
                                            type="number"
                                            size="sm"
                                            placeholder="Max"
                                            value={
                                                tempFilters.deliveryDaysRange?.max !== undefined
                                                    ? String(tempFilters.deliveryDaysRange.max)
                                                    : ''
                                            }
                                            onChange={(e) => handleTempDeliveryRangeMax(e.target.value)}
                                            bg="white"
                                            borderColor="gray.200"
                                            fontSize={checkboxMenuTriggerMetrics.fontSize}
                                        />
                                    </Box>
                                </VStack>
                            </Box>
                        )}
                    </VStack>
                );
        }
    };

    const renderFooter = (showReset: boolean = true) => (
        <MenuFooterActions
            secondaryLabel={showReset ? 'Reset' : 'Cancel'}
            onSecondary={showReset ? handleReset : closePopover}
            onApply={handleApply}
        />
    );

    const filteredCategories = FILTER_CATEGORIES.filter((cat) =>
        !filterSearch || cat.label.toLowerCase().includes(filterSearch.toLowerCase()),
    );

    return (
        <Box ref={containerRef}>
        <FilterBar>
            {activeFilters.map((cat) => (
                <Popover
                    key={cat.key}
                    isOpen={openState === cat.key}
                    placement="bottom-start"
                    closeOnBlur={false}
                >
                    <PopoverTrigger>
                        <FilterChip
                            icon={<Icon as={cat.icon as React.ElementType} boxSize={4} />}
                            label={getFilterLabel(cat.key)}
                            onRemove={() => removeFilter(cat.key)}
                            onClick={() => togglePopover(cat.key)}
                        />
                    </PopoverTrigger>
                    <PopoverContent w="280px" p={0} boxShadow="none" borderRadius="md" data-filter-popover zIndex="popover">
                        <PopoverBody p={0}>
                            {renderFilterOptions(cat.key)}
                            {renderFooter(false)}
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
            ))}

            <AddFilterMenu
                isOpen={openState === 'addFilter'}
                onToggle={() => togglePopover('addFilter')}
                width={addFilterView ? '280px' : '220px'}
                contentProps={{ 'data-filter-popover': '' }}
            >
                        {addFilterView === null ? (
                            <>
                                <CheckboxMenuSearch
                                    value={filterSearch}
                                    onChange={setFilterSearch}
                                    placeholder="Search filters..."
                                />

                                <VStack align="stretch" spacing={0} p="6px">
                                    {filteredCategories.map((cat) => (
                                        <MenuRow
                                            key={cat.key}
                                            kind="drillIn"
                                            summary={getCategorySummary(cat.key)}
                                            icon={<Icon as={cat.icon as React.ElementType} boxSize={4} />}
                                            onClick={() => setAddFilterView(cat.key)}
                                        >
                                            {cat.label}
                                        </MenuRow>
                                    ))}
                                </VStack>

                                {renderFooter(true)}
                            </>
                        ) : (
                            <>
                                <Box px={2} py={2} borderBottom="1px solid" borderColor="gray.100">
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        onClick={() => setAddFilterView(null)}
                                        leftIcon={<Icon as={TbChevronLeft as React.ElementType} />}
                                        color="gray.600"
                                        fontWeight="medium"
                                    >
                                        {FILTER_CATEGORIES.find((c) => c.key === addFilterView)?.label}
                                    </Button>
                                </Box>

                                {renderFilterOptions(addFilterView)}

                                {renderFooter(true)}
                            </>
                        )}
            </AddFilterMenu>
        </FilterBar>
        </Box>
    );
};

export default PartOptionAdvancedFilters;
